import { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { getAuthHeaders } from '@/config/axios.config';
import { environment } from '@/config/environment';
import { useCurrentUserStore } from '@/store/currentUserStore';

export enum SignalEventMethod {
  USER_ACCEPT = 'onUserAccept',
  FORCE_SW_UPDATE = 'onForcedSwUpdate',
  CARGOLIST_UPDATE = 'onCargoListUpdate',
}

interface SignalEventPayload {
  userId?: string;
  groupName?: string;
  isForce?: boolean;
}

type SignalListener<T> = (payload: T) => void;

class SignalRService {
  private hubConnection: HubConnection | null = null;
  private isConnecting = false;
  private retryCount = 0;
  private maxRetries = 5;
  private subscribers = new Map<SignalEventMethod, Set<SignalListener<unknown>>>();

  public async initialize(): Promise<void> {
    if (this.hubConnection || this.isConnecting || !environment.signalRUrl) {
      return;
    }

    this.isConnecting = true;

    const connection = await this.createConnection();
    if (!connection) {
      this.isConnecting = false;
      return;
    }

    try {
      await connection.start();
      this.retryCount = 0;

      connection.onreconnected(() => {
        this.bindAllActiveStreams();
      });

      connection.onclose(() => {
        this.hubConnection = null;
        this.retryWithBackoff();
      });

      this.hubConnection = connection;
      this.bindAllActiveStreams();
    } catch (error) {
      console.error('[SignalR] Failed to connect:', error);
      this.hubConnection = null;
      await connection.stop();
      this.retryWithBackoff();
    } finally {
      this.isConnecting = false;
    }
  }

  private retryWithBackoff(): void {
    if (this.retryCount >= this.maxRetries) {
      console.warn('[SignalR] Max reconnect attempts reached, giving up');
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.retryCount), 30000);
    this.retryCount++;
    setTimeout(() => void this.initialize(), delay);
  }

  public subscribe<T>(methodName: SignalEventMethod, callback: SignalListener<T>): () => void {
    const existingListeners =
      this.subscribers.get(methodName) || new Set<SignalListener<unknown>>();
    existingListeners.add(callback as SignalListener<unknown>);
    this.subscribers.set(methodName, existingListeners);

    if (this.hubConnection) {
      this.bindStream(methodName);
    } else {
      void this.initialize();
    }

    return () => {
      const listeners = this.subscribers.get(methodName);
      if (!listeners) {
        return;
      }

      listeners.delete(callback as SignalListener<unknown>);

      if (listeners.size === 0) {
        this.subscribers.delete(methodName);
        if (this.hubConnection) {
          this.hubConnection.off(methodName);
        }
      }
    };
  }

  public async log(message: string): Promise<void> {
    if (!environment.signalRUrl) {
      return;
    }

    const endpoint = `${environment.signalRUrl.replace(/\/$/, '')}/signalr/log`;

    try {
      const headers = await getAuthHeaders();

      await fetch(endpoint, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });
    } catch (error) {
      console.error('[SignalR] Failed to post log message:', error);
    }
  }

  private bindAllActiveStreams(): void {
    this.subscribers.forEach((_listeners, methodName) => {
      this.bindStream(methodName);
    });
  }

  private bindStream(methodName: SignalEventMethod): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.off(methodName);
    this.hubConnection.on(methodName, (message: string) => {
      this.emitMessage(methodName, message);
    });
  }

  private emitMessage(methodName: SignalEventMethod, message: string): void {
    const payload = this.parsePayload(message);

    if (!this.shouldEmit(payload)) {
      return;
    }

    const listeners = this.subscribers.get(methodName);
    if (!listeners) {
      return;
    }

    listeners.forEach((listener) => {
      listener(payload);
    });
  }

  private parsePayload(message: string): SignalEventPayload {
    try {
      return JSON.parse(message) as SignalEventPayload;
    } catch {
      return {};
    }
  }

  private shouldEmit(payload: SignalEventPayload): boolean {
    const currentUser = useCurrentUserStore.getState().user;

    if (payload.userId && currentUser?.email && payload.userId !== currentUser.email) {
      return false;
    }

    if (payload.groupName) {
      const hasGroup = (currentUser?.userGroups || []).some(
        (group) => group.code === payload.groupName
      );
      if (!hasGroup) {
        return false;
      }
    }

    return true;
  }

  private async createConnection(): Promise<HubConnection | null> {
    const msalInstance = this.getMsalInstance();
    if (!msalInstance) {
      return null;
    }

    const account = msalInstance.getActiveAccount();
    if (!account) {
      return null;
    }

    return new HubConnectionBuilder()
      .withUrl(environment.signalRUrl, {
        headers: {
          'x-ms-signalr-userid': account.username,
        },
        accessTokenFactory: async () => this.acquireAccessToken(msalInstance, account),
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();
  }

  private async acquireAccessToken(
    msalInstance: IPublicClientApplication,
    account: AccountInfo
  ): Promise<string> {
    try {
      const response = await msalInstance.acquireTokenSilent({
        account,
        scopes: [`api://${environment.msalConfig.clientId}/access_api`],
      });

      return response.accessToken;
    } catch {
      return '';
    }
  }

  private getMsalInstance(): IPublicClientApplication | null {
    return (window as { msalInstance?: IPublicClientApplication }).msalInstance || null;
  }
}

export const signalRService = new SignalRService();
